import { useCallback, useEffect, useRef, useState } from "react";
import type { TasteEvent } from "@/services/api/guestTasteApi";

/**
 * P216 T0304 (client half) — THE RUN'S OWN EVENTS, SUBSCRIBED (P100 D-029 steps 2–3)
 *
 * D-029's concrete delta is *"the socket + the board's subscription"*. The socket is built and its
 * contract is green (`tests/project100/test_guest_taste_stream.py`); this is the subscription: a surface
 * that reacts to a unit of work WHEN IT LANDS instead of asking for it every two seconds.
 *
 * THE CREDENTIAL IS THE CLAIM TOKEN, carried in the query string, because a browser cannot set an
 * Authorization header on a WebSocket and the visitor has no account yet — the same reason the backend
 * endpoint takes it there. No run in hand, no token, no connection: nothing is attempted.
 *
 * `live` IS THE HONEST SWITCH. The caller polls until the socket is actually OPEN, and stops the moment
 * it is. A socket that cannot connect must never leave a surface silently frozen, so this hook reports
 * whether it is live rather than pretending a connection is a guarantee — a subscriber that assumes one
 * is how a screen goes quiet in the one case that matters.
 *
 * Its shape is the house pattern's (`useSetupRunWS.ts`): token in the query, bounded retries with
 * backoff, and every handler guarded by `mountedRef` so an unmount mid-frame cannot set state.
 */

const MAX_RETRIES = 3;
const RETRY_BASE_MS = 1500;

function buildStreamUrl(jobId: string, token: string): string {
  const raw = (import.meta.env.VITE_WS_URL as string | undefined)
    || (import.meta.env.VITE_API_URL as string | undefined)
    || window.location.origin;
  const base = raw.replace(/^http/, "ws").replace(/\/+$/, "");
  return `${base}/api/v1/guest/taste/${encodeURIComponent(jobId)}/stream?token=${encodeURIComponent(token)}`;
}

export interface TasteStream {
  /** Every unit of work the socket has delivered, in order. */
  events: TasteEvent[];
  /** `done` or `error`, once the run has said everything it will say. */
  finalState: string | null;
  /** Reached the service at least once — the caller may stop its fallback poll. */
  live: boolean;
  /** The run is gone from the engine (expired). Nothing to show, nothing to say. */
  gone: boolean;
}

export function useGuestTasteStream(jobId: string | null, claimToken: string | null): TasteStream {
  const [events, setEvents] = useState<TasteEvent[]>([]);
  const [finalState, setFinalState] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [gone, setGone] = useState(false);

  const mountedRef = useRef(true);
  const socketRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  /**
   * THE END OF THE RUN IS READ FROM REFS, NOT FROM STATE, and that is what stops the reconnect loop the
   * first live run exposed: `connect` is memoised on its dependencies, so a dependency it also SETS makes
   * every frame rebuild it, which re-runs the effect, which opens another socket. Measured: eleven sockets
   * and eighty-eight frames for ten units. A value a callback both reads and writes must not be its
   * dependency.
   */
  const finalStateRef = useRef<string | null>(null);
  const goneRef = useRef(false);

  const connect = useCallback(() => {
    if (!jobId || !claimToken) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(buildStreamUrl(jobId, claimToken));
    } catch {
      return; // a malformed URL is not a retryable condition
    }
    socketRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      retriesRef.current = 0;
      setLive(true);
    };

    ws.onmessage = (m: MessageEvent) => {
      if (!mountedRef.current) return;
      let frame: Record<string, unknown>;
      try {
        frame = JSON.parse(String(m.data)) as Record<string, unknown>;
      } catch {
        return; // a frame we cannot read is not a frame we act on
      }
      if (frame.event === "unit") {
        const { event: _e, job_id: _j, ...unit } = frame;
        // A RECONNECTED SOCKET RE-SENDS THE RUN'S HISTORY (the server's cursor is per connection), so a
        // frame already held is dropped rather than appended again: `at` is the elapsed second the unit
        // finished at, which is unique per unit by construction.
        const at = (unit as { at?: number }).at;
        setEvents((prev) =>
          prev.some((e) => (e as { at?: number }).at === at) ? prev : [...prev, unit as unknown as TasteEvent],
        );
        return;
      }
      if (frame.event === "state") {
        const state = String(frame.state ?? "done");
        finalStateRef.current = state;
        setFinalState(state);
        return;
      }
      if (frame.event === "gone") {
        goneRef.current = true;
        setGone(true);
        return;
      }
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      // A closed stream is not by itself a failure: the run may simply have ended. Retry only while the
      // run could still be producing, and only a bounded number of times.
      if (finalStateRef.current !== null || goneRef.current || retriesRef.current >= MAX_RETRIES) return;
      retriesRef.current += 1;
      retryTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) connect();
      }, RETRY_BASE_MS * retriesRef.current);
    };

    ws.onerror = () => {
      /* `onclose` follows; one handler decides what to do about a drop. */
    };
  }, [jobId, claimToken]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
      const ws = socketRef.current;
      socketRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000);
      }
    };
  }, [connect]);

  return { events, finalState, live, gone };
}
